import moment from 'moment';

import type { EmissionFactor, EntityLabel, ReportingPeriod, Standard } from '@esg/esg-global-types';
import type { MetricRecordFilters, ResolverQueryRequestMetadata } from '@esg/syntiro-api-core';

import type { MetricExtended } from '../metric_capture/metric';
import { getEmissionFactors } from '../../graphql/services/emission_factor.service';
import { getMetricRecords } from '../../graphql/services/metric_record.service';

export interface EmissionFactorStandard extends EmissionFactor {
  standard: Standard;
}

export const emissionFactorLabel: EntityLabel = {
  one: 'Emission Factor',
  many: 'Emission Factors'
};

/**
 * Returns all non-deleted emission factors for the given metrics, optionally reduced to the
 * latest factor per metric (by start_date).
 * @param metadata Request metadata containing group_id and company_id (or master_list flag).
 * @param metrics List of metrics to fetch emission factors for.
 * @param latestOnly When true, returns only the latest emission factor per metric.
 */
export async function getEmissionFactorsForMetrics(
  metadata: ResolverQueryRequestMetadata,
  metrics: MetricExtended[],
  latestOnly = false
): Promise<EmissionFactor[]> {
  if (!metrics.length) return [];
  const emissionFactors = await getEmissionFactors(metadata, {
    deleted: false,
    metric_ids: metrics.map((metric) => metric.id)
  });
  if (!latestOnly) return emissionFactors;

  const processedMetricIds = new Set<string>();
  return emissionFactors
    .sort((a, b) => moment(b.start_date).diff(moment(a.start_date)))
    .filter(({ metric }) => {
      if (!metric?.id || processedMetricIds.has(metric.id)) return false;
      processedMetricIds.add(metric.id);
      return true;
    });
}

/**
 * Selects the most recently started emission factor from a pre-fetched list that matches the
 * given metric and is in effect on or before the reporting period's end date.
 * When countryId is supplied, prefers a country-specific EF and falls back to the global
 * (country == null) EF. When countryId is absent, returns the latest global EF only.
 * @param emissionFactors Pre-fetched list of emission factors to search through.
 * @param metric Metric to match emission factors against.
 * @param reportingPeriod Reporting period whose end date determines factor eligibility.
 * @param countryId Optional country ID — prefers a country-specific EF with global fallback.
 */
export function getEmissionFactorForMetricRecordParameters(
  emissionFactors: EmissionFactor[],
  metric: MetricExtended,
  reportingPeriod: ReportingPeriod,
  countryId?: string | null
): EmissionFactor | null {
  const sortedEmissionFactors = emissionFactors
    .filter(
      (emissionFactor) =>
        emissionFactor.metric?.id === metric.id &&
        moment(emissionFactor.start_date).isSameOrBefore(moment(reportingPeriod.end))
    )
    .sort((a, b) => moment(b.start_date).diff(moment(a.start_date)));

  if (countryId) {
    let globalFallbackEmissionFactor: EmissionFactor | null = null;
    for (const emissionFactor of sortedEmissionFactors) {
      if (emissionFactor.country?.id === countryId) return emissionFactor;
      if (emissionFactor.country == null) globalFallbackEmissionFactor ??= emissionFactor;
    }
    return globalFallbackEmissionFactor;
  }
  return sortedEmissionFactors.find((emissionFactor) => emissionFactor.country == null) ?? null;
}

/**
 * Returns true if no non-deleted metric records reference the given emission factor, false if
 * any do. Short-circuits to true for master-list mode — master-list EFs are not directly
 * referenced by company metric records.
 * @param row The emission factor row to check.
 * @param groupId Group ID of the company.
 * @param companyId Company ID.
 */
export async function allowDeleteEmissionFactor(
  row: EmissionFactor,
  groupId: string,
  companyId: string
): Promise<boolean> {
  const metricRecordfilters: MetricRecordFilters = {
    emission_factor_id: row.id,
    metric_group_id: row.metric_group?.id,
    metric_id: row.metric?.id,
    deleted: false
  };
  const referencingMetricRecords = await getMetricRecords(
    { group_id: groupId, company_id: companyId },
    metricRecordfilters
  );
  return referencingMetricRecords.length === 0;
}
